import { Avatar } from "@its-engineering-and-development/ui-foundation/components";
import type { AvatarProps } from "@its-engineering-and-development/ui-foundation/components";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";

/** Number of rotating avatar colors (user 6 wraps back to color 1). */
export const PERMISSION_AVATAR_COLOR_COUNT = 5;

/**
 * Fixed hex palette — theme bg-utility-*-500 tokens are not all generated in this app build
 * (only warning/success/error/brand/gray exist). Arbitrary colors guarantee every user gets a badge.
 */
const PERMISSION_AVATAR_BACKGROUNDS = [
    "bg-[#F97316]", // orange
    "bg-[#EAB308]", // yellow
    "bg-[#22C55E]", // green
    "bg-[#3B82F6]", // blue
    "bg-[#A855F7]", // purple
] as const;

const AVATAR_INITIALS_CLASS: Record<NonNullable<AvatarProps["size"]>, string> = {
    xxs: "text-[10px] font-semibold leading-none text-white",
    xs: "text-xs font-semibold leading-none text-white",
    sm: "text-sm font-semibold leading-none text-white",
    md: "text-md font-semibold leading-none text-white",
    lg: "text-lg font-semibold leading-none text-white",
    xl: "text-xl font-semibold leading-none text-white",
    "2xl": "text-display-xs font-semibold leading-none text-white",
};

export function getPermissionAvatarColorClass(colorIndex: number) {
    return PERMISSION_AVATAR_BACKGROUNDS[colorIndex % PERMISSION_AVATAR_COLOR_COUNT];
}

export function buildPermissionUserColorIndexMap(users: { id: string }[]) {
    const map = new Map<string, number>();
    users.forEach((user, index) => {
        map.set(user.id, index % PERMISSION_AVATAR_COLOR_COUNT);
    });
    return map;
}

export function getPermissionUserInitials(name: string, initials?: string) {
    if (initials?.trim()) {
        return initials.trim().toUpperCase().slice(0, 2);
    }

    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
    }

    return name.trim().slice(0, 2).toUpperCase();
}

export function PermissionUserAvatar({
    user,
    colorIndex,
    size = "xs",
    className,
}: {
    user: { id: string; name: string; initials?: string };
    colorIndex: number;
    size?: AvatarProps["size"];
    className?: string;
}) {
    const displayInitials = getPermissionUserInitials(user.name, user.initials);

    return (
        <Avatar
            size={size}
            alt={user.name}
            contrastBorder={false}
            className={cx("shrink-0", getPermissionAvatarColorClass(colorIndex), className)}
            placeholder={<span className={AVATAR_INITIALS_CLASS[size ?? "xs"]}>{displayInitials}</span>}
        />
    );
}
